import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { isUpcoming, shortDate, sortEvents } from "../lib/format.js";
import { EventForm, Field, MemberForm, emptyEvent, emptyMember } from "./forms.jsx";

const TABS = [
  { id: "anfragen", label: "Anfragen" },
  { id: "events", label: "Anlässe" },
  { id: "members", label: "Mitglieder" },
  { id: "benefits", label: "Nutzen" },
  { id: "settings", label: "Gruppe" },
  { id: "zugang", label: "Zugang" },
];

const STATUS_LABEL = {
  neu: "neu",
  kontaktiert: "kontaktiert",
  aufgenommen: "aufgenommen",
  abgelehnt: "abgelehnt",
};

export default function Admin({ data, setData, notify, onExit }) {
  const openCount = data.applications.filter((a) => a.status === "neu").length;
  const [tab, setTab] = useState(openCount ? "anfragen" : "events");
  const [editEv, setEditEv] = useState(null);
  const [editMe, setEditMe] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);

  /** Ruft die Schnittstelle auf und übernimmt den zurückgegebenen Stand. */
  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      const next = await fn();
      if (next && next.settings) setData(next);
      if (okMessage) notify(okMessage);
      return next;
    } catch (ex) {
      notify(ex.message, "error");
      if (ex.status === 401) onExit(true);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const events = [...data.events].sort(sortEvents.past);
  const members = [...data.members].sort((a, b) => a.name.localeCompare(b.name, "de"));
  const apps = [...data.applications].sort((a, b) => String(b.at).localeCompare(String(a.at)));

  return (
    <div className="admin">
      <div className="wrap">
        <h1>Verwaltung</h1>
        <p className="admin-sub">Was hier gespeichert wird, steht sofort auf der Seite.</p>

        {data.security?.isInitialPassword ? (
          <div className="note note-warn">
            <strong>Das Startpasswort ist noch aktiv.</strong> Bitte unter „Zugang“ ein eigenes
            Passwort setzen, sonst kommt jede Person mit dem Standardcode in die Verwaltung.
          </div>
        ) : null}

        <div className="tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              className="tab"
              data-on={tab === t.id}
              onClick={() => {
                setTab(t.id);
                setConfirmId(null);
              }}
            >
              {t.label}
              {t.id === "anfragen" && openCount ? (
                <span className="tab-badge">{openCount}</span>
              ) : null}
            </button>
          ))}
        </div>

        {tab === "anfragen" ? (
          <div className="panel">
            <h3>Anmeldungen von Firmen</h3>
            {apps.length === 0 ? (
              <p className="empty">Noch keine Anmeldung eingegangen.</p>
            ) : (
              apps.map((a) => (
                <div className="item" key={a.id}>
                  <div style={{ minWidth: 240, flex: "1 1 260px" }}>
                    <b>
                      {a.company}
                      <span className="status" data-s={a.status}>
                        {STATUS_LABEL[a.status] || a.status}
                      </span>
                    </b>
                    <small>
                      {[a.person, a.branch].filter(Boolean).join(" · ") || "ohne weitere Angaben"}
                      <br />
                      <a href={`mailto:${a.email}`}>{a.email}</a>
                      {a.note ? (
                        <>
                          <br />
                          {a.note}
                        </>
                      ) : null}
                      <br />
                      Eingegangen am {a.at}
                    </small>
                  </div>
                  <div className="acts">
                    {a.status === "neu" ? (
                      <>
                        <button
                          className="b"
                          disabled={busy}
                          onClick={() =>
                            run(() => api.acceptApplication(a.id), `${a.company} ist jetzt Mitglied.`)
                          }
                        >
                          Als Mitglied aufnehmen
                        </button>
                        <button
                          className="b b-ghost"
                          disabled={busy}
                          onClick={() =>
                            run(() => api.setApplicationStatus(a.id, "kontaktiert"), "Als kontaktiert vermerkt.")
                          }
                        >
                          Kontaktiert
                        </button>
                        <button
                          className="b b-ghost"
                          disabled={busy}
                          onClick={() =>
                            run(() => api.setApplicationStatus(a.id, "abgelehnt"), "Abgelehnt.")
                          }
                        >
                          Abgelehnt
                        </button>
                      </>
                    ) : null}
                    {confirmId === a.id ? (
                      <button
                        className="b b-del"
                        disabled={busy}
                        onClick={async () => {
                          await run(() => api.deleteApplication(a.id), "Anmeldung gelöscht.");
                          setConfirmId(null);
                        }}
                      >
                        Wirklich löschen
                      </button>
                    ) : (
                      <button className="b b-ghost" onClick={() => setConfirmId(a.id)}>
                        Löschen
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : null}

        {tab === "events" ? (
          <>
            {editEv ? (
              <EventForm
                value={editEv}
                members={members}
                busy={busy}
                onCancel={() => setEditEv(null)}
                onSave={async (ev) => {
                  const next = await run(
                    () => (ev.id ? api.updateEvent(ev) : api.createEvent(ev)),
                    "Anlass gespeichert."
                  );
                  if (next) setEditEv(null);
                }}
              />
            ) : (
              <button
                className="b"
                style={{ marginBottom: 16 }}
                onClick={() => setEditEv(emptyEvent())}
              >
                Anlass hinzufügen
              </button>
            )}

            <div className="panel">
              {events.length === 0 ? (
                <p className="empty">Noch kein Anlass erfasst.</p>
              ) : (
                events.map((e) => (
                  <div className="item" key={e.id}>
                    <div style={{ flex: "1 1 260px" }}>
                      <b>{e.title}</b>
                      <small>
                        {shortDate(e.date)}
                        {e.location ? ` · ${e.location}` : ""}
                        {isUpcoming(e.date) ? "" : " · vergangen"}
                      </small>
                    </div>
                    <div className="acts">
                      <button className="b b-ghost" onClick={() => setEditEv(e)}>
                        Bearbeiten
                      </button>
                      {confirmId === e.id ? (
                        <button
                          className="b b-del"
                          disabled={busy}
                          onClick={async () => {
                            await run(() => api.deleteEvent(e.id), "Anlass gelöscht.");
                            setConfirmId(null);
                          }}
                        >
                          Wirklich löschen
                        </button>
                      ) : (
                        <button className="b b-ghost" onClick={() => setConfirmId(e.id)}>
                          Löschen
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : null}

        {tab === "members" ? (
          <>
            {editMe ? (
              <MemberForm
                value={editMe}
                busy={busy}
                onCancel={() => setEditMe(null)}
                onSave={async (m) => {
                  const next = await run(
                    () => (m.id ? api.updateMember(m) : api.createMember(m)),
                    "Mitglied gespeichert."
                  );
                  if (next) setEditMe(null);
                }}
              />
            ) : (
              <button
                className="b"
                style={{ marginBottom: 16 }}
                onClick={() => setEditMe(emptyMember())}
              >
                Mitglied hinzufügen
              </button>
            )}

            <div className="panel">
              {members.length === 0 ? (
                <p className="empty">Noch keine Firma erfasst.</p>
              ) : (
                members.map((m) => (
                  <div className="item" key={m.id}>
                    <div style={{ flex: "1 1 260px" }}>
                      <b>{m.name}</b>
                      <small>
                        {[m.branch, m.city, m.contact].filter(Boolean).join(" · ") ||
                          "ohne weitere Angaben"}
                      </small>
                    </div>
                    <div className="acts">
                      <button className="b b-ghost" onClick={() => setEditMe(m)}>
                        Bearbeiten
                      </button>
                      {confirmId === m.id ? (
                        <button
                          className="b b-del"
                          disabled={busy}
                          onClick={async () => {
                            await run(() => api.deleteMember(m.id), "Mitglied gelöscht.");
                            setConfirmId(null);
                          }}
                        >
                          Wirklich löschen
                        </button>
                      ) : (
                        <button className="b b-ghost" onClick={() => setConfirmId(m.id)}>
                          Löschen
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : null}

        {tab === "benefits" ? <BenefitsPanel data={data} run={run} busy={busy} /> : null}
        {tab === "settings" ? <SettingsPanel data={data} run={run} busy={busy} /> : null}
        {tab === "zugang" ? <AccessPanel data={data} run={run} busy={busy} /> : null}

        <button className="b b-ghost" style={{ marginTop: 14 }} onClick={() => onExit()}>
          Zurück zur Seite
        </button>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Nutzen-Karten                                                     */
/* ================================================================== */

function BenefitsPanel({ data, run, busy }) {
  const [list, setList] = useState(data.benefits);
  useEffect(() => setList(data.benefits), [data.benefits]);

  const set = (i, k) => (e) =>
    setList(list.map((b, j) => (i === j ? { ...b, [k]: e.target.value } : b)));

  return (
    <div className="panel">
      <h3>Die drei Karten auf der Startseite</h3>
      <p className="panel-hint">
        Sie stehen im Abschnitt „Weshalb der Aufwand sich lohnt“ und werden fortlaufend nummeriert.
      </p>

      {list.map((b, i) => (
        <div key={b.id} style={{ marginBottom: 18 }}>
          <Field label={`Karte ${String(i + 1).padStart(2, "0")} · Titel`} id={`b-t-${i}`}>
            <input id={`b-t-${i}`} value={b.title} onChange={set(i, "title")} />
          </Field>
          <Field label="Text" id={`b-x-${i}`}>
            <textarea id={`b-x-${i}`} value={b.text} onChange={set(i, "text")} />
          </Field>
          <div className="acts">
            <button
              className="b b-del"
              disabled={busy}
              onClick={() => setList(list.filter((_, j) => j !== i))}
            >
              Karte entfernen
            </button>
          </div>
        </div>
      ))}

      <div className="acts">
        <button
          className="b"
          disabled={busy}
          onClick={() => run(() => api.saveBenefits(list), "Karten gespeichert.")}
        >
          Karten speichern
        </button>
        {list.length < 6 ? (
          <button
            className="b b-ghost"
            onClick={() =>
              setList([...list, { id: `neu-${Date.now()}`, title: "", text: "" }])
            }
          >
            Karte hinzufügen
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Angaben zur Gruppe                                                */
/* ================================================================== */

function SettingsPanel({ data, run, busy }) {
  const [s, setS] = useState(data.settings);
  useEffect(() => setS(data.settings), [data.settings]);
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const toggle = (k) => (e) => setS({ ...s, [k]: e.target.checked });

  return (
    <>
      <div className="panel">
        <h3>Angaben zur Gruppe</h3>

        <div className="row2">
          <Field label="Name" id="s-n">
            <input id="s-n" value={s.groupName} onChange={set("groupName")} />
          </Field>
          <Field label="Region" id="s-r">
            <input id="s-r" value={s.region} onChange={set("region")} />
          </Field>
        </div>

        <Field label="Titel auf der Startseite" id="s-h">
          <input id="s-h" value={s.headline} onChange={set("headline")} />
        </Field>

        <Field label="Einleitungstext" id="s-c">
          <textarea id="s-c" value={s.claim} onChange={set("claim")} />
        </Field>

        <div className="row2">
          <Field label="Kontakt-Mail" id="s-m">
            <input id="s-m" type="email" value={s.contact} onChange={set("contact")} />
          </Field>
          <Field label="Gegründet" id="s-g">
            <input id="s-g" value={s.founded} onChange={set("founded")} placeholder="2021" />
          </Field>
        </div>

        <div className="row2">
          <Field label="LinkedIn-Gruppe" id="s-l">
            <input id="s-l" value={s.linkedin} onChange={set("linkedin")} />
          </Field>
          <Field label="Anlässe pro Jahr" id="s-e" hint="Erscheint als Kennzahl auf der Startseite.">
            <input id="s-e" value={s.eventsPerYear} onChange={set("eventsPerYear")} placeholder="6–8" />
          </Field>
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={s.showNextEventCard !== false}
            onChange={toggle("showNextEventCard")}
          />
          <span>Karte „Nächster Anlass“ auf der Startseite zeigen</span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={s.showPastEvents !== false}
            onChange={toggle("showPastEvents")}
          />
          <span>Vergangene Anlässe zum Aufklappen anbieten</span>
        </label>

        <div className="acts">
          <button
            className="b"
            disabled={busy}
            onClick={() => run(() => api.saveSettings(s), "Angaben gespeichert.")}
          >
            Angaben speichern
          </button>
        </div>
      </div>

      <div className="panel">
        <h3>Daten sichern</h3>
        <p className="panel-hint">
          Die Sicherung enthält Anlässe, Mitglieder, Anmeldungen und Einstellungen – ohne Passwort.
        </p>
        <div className="acts">
          <a className="b" href="/api/admin/export">
            Als Datei herunterladen
          </a>
          <button
            className="b b-ghost"
            disabled={busy}
            onClick={() => run(() => api.backup(), "Kopie auf dem Server abgelegt.")}
          >
            Kopie auf dem Server ablegen
          </button>
        </div>
      </div>
    </>
  );
}

/* ================================================================== */
/*  Passwort                                                          */
/* ================================================================== */

function AccessPanel({ data, run, busy }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (next.length < 10) return setErr("Das neue Passwort braucht mindestens 10 Zeichen.");
    if (next !== repeat) return setErr("Die beiden neuen Passwörter sind nicht gleich.");
    const ok = await run(() => api.changePassword(current, next), "Passwort geändert.");
    if (ok) {
      setCurrent("");
      setNext("");
      setRepeat("");
    }
  };

  return (
    <div className="panel">
      <h3>Passwort der Verwaltung</h3>
      <p className="panel-hint">
        {data.security?.passwordChangedAt
          ? `Zuletzt geändert am ${data.security.passwordChangedAt}.`
          : "Es ist noch das Startpasswort gesetzt."}{" "}
        Es gilt für alle im Vorstand gemeinsam.
      </p>

      <Field label="Bisheriges Passwort" id="p-a">
        <input
          id="p-a"
          type="password"
          value={current}
          autoComplete="current-password"
          onChange={(e) => {
            setCurrent(e.target.value);
            setErr("");
          }}
        />
      </Field>

      <div className="row2">
        <Field label="Neues Passwort" id="p-b" hint="Mindestens 10 Zeichen.">
          <input
            id="p-b"
            type="password"
            value={next}
            autoComplete="new-password"
            onChange={(e) => {
              setNext(e.target.value);
              setErr("");
            }}
          />
        </Field>
        <Field label="Neues Passwort wiederholen" id="p-c">
          <input
            id="p-c"
            type="password"
            value={repeat}
            autoComplete="new-password"
            onChange={(e) => {
              setRepeat(e.target.value);
              setErr("");
            }}
          />
        </Field>
      </div>

      <div className="acts">
        <button className="b" disabled={busy || !current || !next} onClick={save}>
          Passwort ändern
        </button>
      </div>
      {err ? (
        <p className="form-error" role="alert">
          {err}
        </p>
      ) : null}
    </div>
  );
}
